
import React, { useState, useEffect, useMemo } from 'react';
import { HomeworkConfig, LanguageOption } from '../types';
import { SUPPORTED_LANGUAGES } from '../constants';
import Button from './Button';
import SelectInput from './SelectInput';
import CheckIcon from './icons/CheckIcon';
import { useTranslation } from 'react-i18next';

interface ConfigurationViewProps {
  problemImage: string;
  initialConfig: HomeworkConfig;
  onSubmit: (config: HomeworkConfig, saveConfig: boolean) => void;
}

const ConfigurationView: React.FC<ConfigurationViewProps> = ({ problemImage, initialConfig, onSubmit }) => {
  const { t, i18n } = useTranslation();
  const gradeOptions: LanguageOption[] = useMemo(() =>
    (t('configurationView.gradeOptions', { returnObjects: true }) as { label: string, value: string }[]).map(opt => ({ ...opt, flag: '' })),
    [t]
  );

  const getInitialLanguage = () => {
    if (initialConfig.language) return initialConfig.language;

    const browserLanguage = i18n.language.split('-')[0]; // 'en-US' -> 'en'
    const isSupported = SUPPORTED_LANGUAGES.some(lang => lang.value === browserLanguage);
    return isSupported ? browserLanguage : SUPPORTED_LANGUAGES[0].value;
  };

  const [grade, setGrade] = useState<string>(initialConfig.grade || gradeOptions[0].value);
  const [language, setLanguage] = useState<string>(getInitialLanguage());
  const [saveConfig, setSaveConfig] = useState<boolean>(true); // Default to true for convenience
  const [error, setError] = useState<string>('');

  useEffect(() => {
    setGrade(initialConfig.grade || gradeOptions[0].value);
    setLanguage(getInitialLanguage());
  }, [initialConfig, gradeOptions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!grade) {
      setError(t('configurationView.gradeRequiredError'));
      return;
    }
    if (!language) {
      setError(t('configurationView.languageRequiredError'));
      return;
    }
    setError('');
    onSubmit({ grade, language }, saveConfig);
  };

  return (
    <div className="bg-white p-4 sm:p-6 md:p-8 shadow-xl rounded-lg">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-sky-700 mb-8">{t('configurationView.title')}</h2>
      <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
          <div className="w-full md:w-1/3 flex-shrink-0">
            <p className="block text-sm font-medium text-slate-700 mb-1">{t('configurationView.problemImageLabel')}</p>
            <img src={problemImage} alt="Problem preview" className="rounded-lg border border-slate-300 object-contain max-h-60 sm:max-h-72 w-full" />
          </div>
          <div className="w-full md:w-2/3 space-y-6">
            <div>
              <label htmlFor="grade" className="block text-sm font-medium text-slate-700 mb-1">
                {t('configurationView.gradeLabel')} <span className="text-red-500">*</span>
              </label>
              <SelectInput
                id="grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                options={gradeOptions}
                required
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-slate-700 mb-1">
                {t('configurationView.explanationLanguageLabel')} <span className="text-red-500">*</span>
              </label>
              <SelectInput
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                options={SUPPORTED_LANGUAGES}
                className="w-full"
                required
              />
            </div>
            <div className="flex items-center">
              <input
                id="saveConfig"
                type="checkbox"
                checked={saveConfig}
                onChange={(e) => setSaveConfig(e.target.checked)}
                className="h-4 w-4 text-sky-600 border-slate-300 rounded focus:ring-sky-500"
              />
              <label htmlFor="saveConfig" className="ml-2 block text-sm text-slate-700">
                {t('configurationView.saveConfigLabel')}
              </label>
            </div>
          </div>
        </div>
        
        {error && <p className="text-red-500 text-sm text-center">{error}</p>}

        <div className="pt-4 text-center">
          <Button type="submit" size="lg" className="w-full md:w-auto">
            <CheckIcon className="w-5 h-5 mr-2" />
            {t('configurationView.submitButton')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ConfigurationView;
    